<template>
  <SSection
    v-if="owner"
    :desktop-full="true"
    :mobile-full="true"
    :title="t('views.cottage-prices.owner.title')"
  >
    <TAvatar
      :img-prop="imgProp"
      size="lg"
      variant="image"
      :title="owner.name"
      class="mt-8 mb-4"
    />

    <TProse :responsive="false">
      <p>{{ owner?.description }}</p>
      <ul>
        <li v-if="calendarUpdate">
          {{ calendarUpdate }}
        </li>
        <li v-if="owner.acceptanceRate">
          {{
            t('views.cottage-prices.owner.acceptanceRate', {
              rate: owner.acceptanceRate,
            })
          }}
        </li>
        <li v-if="owner.answer && owner.answer.rate !== null">
          {{
            owner.answer.rate > 0
              ? t('views.cottage-prices.owner.answerRate', {
                  rate: owner.answer.rate,
                  range: t(`range.${owner.answer.range}`),
                  value: owner.answer.value,
                  unit: t(`time.${owner.answer.unit}`, owner.answer.value),
                })
              : t('views.cottage-prices.owner.noAnswerRate')
          }}
        </li>
        <li v-if="languages">
          {{ t('views.cottage-prices.owner.languages') }}: {{ languages }}.
        </li>
      </ul>
    </TProse>

    <div class="flex flex-col mt-3 gap-y-3 gap-x-4 lg:flex-row">
      <UButton
        :ui="mobileFullWidthButton"
        color="neutral"
        variant="solid"
        size="sm"
        :label="t('views.cottage-prices.owner.sendMessage')"
        icon="i-heroicons-envelope"
        :to="goToCottageContact"
        rel="nofollow"
        external
      />
      <UButton
        v-if="cottage?.salesService.isAllowedToShowPhone"
        :ui="mobileFullWidthButton"
        color="neutral"
        variant="solid"
        size="sm"
        :label="t('views.cottage-prices.owner.showPhone')"
        icon="i-heroicons-phone"
        :to="goToCottagePhones"
        rel="nofollow"
        external
      />
      <UButton
        v-if="cottage?.salesService.isAllowedToShowWhatsapp"
        :ui="mobileFullWidthButton"
        color="neutral"
        variant="solid"
        size="sm"
        :label="t('views.cottage-prices.owner.sendWhatsapp')"
        icon="i-ic-baseline-whatsapp"
        :to="goToCottageContactWhatsApp"
        rel="nofollow"
        external
      />
    </div>
  </SSection>
</template>

<script setup lang="ts">
import { usePricesStore } from '~~/modules/views/cottage-prices/stores/pricesStore'
import type { TLazyimg_Props } from '~~/modules/design-system/components/t-lazyimg/constants/types'

import { addConnectorsToList } from '~~/modules/lang/composables/useLanguageFormatter'

import { mobileFullWidthButton } from '~~/modules/design-system/utils/nuxt-ui-config/components/button'
import { formatCottageCalendarUpdate } from '../utils/formatCottageCalendarUpdate'

const route = useRoute()
const pricesStore = usePricesStore()

// Get cottage from store instead of props
const cottage = computed(() => pricesStore.getCottageDetail)
const owner = computed(() => pricesStore.cottageOwnerDetail)

const imgProp: TLazyimg_Props = {
  src: owner.value?.picture as string,
  alt: owner.value?.name || '',
}

const languagesList =
  cottage.value?.features?.languages?.map(lang => lang.name) ?? []

const calendarUpdate = cottage.value?.calendarUpdate
  ? formatCottageCalendarUpdate(
      cottage.value.calendarUpdate.daysSinceLastUpdate,
    )
  : ''

const languages = languagesList.length ? addConnectorsToList(languagesList) : ''

const goToCottageContact = routes().cottageContact({
  slug: route.params.slug as string,
  province: route.params.province as string,
})

const goToCottagePhones = routes().cottagePhones({
  slug: route.params.slug as string,
  province: route.params.province as string,
})

const goToCottageContactWhatsApp = routes().cottageContactWhatsApp({
  slug: route.params.slug as string,
  province: route.params.province as string,
})
</script>
